import React, { useEffect, useMemo, useState, useRef } from 'react'
import { useReactToPrint } from 'react-to-print'
import * as styles from './checkoutModal.module.scss'
import { Row, Col } from 'react-grid-system'
import PrintableShipTicket from '../common/printableShipTicket/printableShipTicket'

import * as yup from 'yup'

import { Alert, Button, LoadingOverlay, Modal, Svg } from '../common'

import {
   useActiveLocations,
   useForm,
   useMassTransferConnections,
   useSettings,
   useWarehouses,
   useWorkflow,
} from '../../hooks'
import {
   convertConnectionsToOptions,
   convertLocationToOptions,
} from '../common/filters/helpers'
import {
   BorrowView,
   LoanAndFieldTransferView,
   ReturnView,
   SuccessView,
   WarehouseTransferView,
   CommentOnlyView
} from './checkoutModalViews'
import { Item, Severity, ToolStatus, ToolTypes } from '../../types'
import { PATH } from '../../constants/global'
import { exportToExcel } from '../../utils/functions'
import { generateExcelData } from '../views/actions/helpers'
import {
   CartOptions,
   CheckoutModalProps,
   ConnectionRequestType,
   View,
} from './actionsWorkflow.type'
import { ErrorView } from './checkoutModalViews/errorView'
import { formatDateForDisplay } from '../../utils/datetime'
import { Option } from '../common/form/fields/dropdown/types'
import { convertWarehouseOptionToOption } from '../common/crudItems/inventory/helpers'
import { SyntheticInputEvent } from 'react-number-format/types/types'

const errorMsg = 'This field is required'

const loanAndFieldTransferSchema = yup.object().shape({
   Connection: yup.string().required(errorMsg),
   Comment: yup.string(),
})

const returnSchema = yup.object().shape({
   Return: yup.string().required(),
   Warehouse: yup.string().required(errorMsg),
   Comment: yup.string(),
})

const returnNoWarehouseSchema = yup.object().shape({
   Return: yup.string().required(),
})

const warehouseTransferSchema = yup.object().shape({
   NewLocation: yup.string().required(),
   Comment: yup.string(),
})
const warehouseTransferNoLocationSchema = yup.object().shape({})

const borrowSchema = yup.object().shape({
   NewLocation: yup.string(),
})
const borrowNoLocationSchema = yup.object().shape({
   DueBackDate: yup.string(),
})
const availableSchema = yup.object().shape({
   Available: yup.string(),
})

const cartOptionsForm: CartOptions = {
   connection: null,
   comment: null,
   dueBackDate: '',
   location: '',
   selectedLocationId: null,
   returnLocationId: null,
}

const CheckoutModal = ({
   isModalOpen,
   handleClose,
   checkoutAction,
   selectedItems,
   viewTitle,
   handlePageCancelAndReset,
   isAdminCompanyUser,
   quantityInCart,
   handleWorkflowSuccess,
   selectedWarehouse,
   selectedTransferToWarehouse,
   isActionsPage,
}: CheckoutModalProps) => {
   const {
      postWorkflowRequest,
      isWorkflowPostSuccess,
      isPostingRequest,
      workflowResponse,
      isWorkflowError,
   } = useWorkflow()
   const [view, setView] = useState<string>(checkoutAction?.label)
   const [modalMsg, setModalMsg] = useState<string | JSX.Element>('')
   const [confirmButtonMsg, setConfirmButtonMsg] = useState<string>('')
   const [validationSchema, setValidationSchema] =
      useState<yup.AnyObjectSchema>(loanAndFieldTransferSchema)
   const [cartOptions, setCartOptions] = useState<CartOptions>(cartOptionsForm)
   const [connectionName, setConnectionName] = useState<string>('')
   const [dueBackDateString, setDueBackDateString] = useState<string>('')
   const printContentRef = useRef<HTMLDivElement>(null)
   const reactToPrintFn = useReactToPrint({ 
      contentRef: printContentRef,
   })

   const connectionRequestType = useMemo(() => {
      switch (view) {
         case View.Loan:
            return ConnectionRequestType.LoanRequest
         case View.Field_Transfer:
            return ConnectionRequestType.TransferRequest
         default:
            return null
      }
   }, [view])

   const selectedItemId = useMemo(() => {
      if (selectedItems?.length === 0) return null
      const { Id, Warehouse } = selectedItems[0]
      return selectedItems.every(
         (obj) =>
            obj?.Warehouse?.WarehouseLocationId ===
            Warehouse?.WarehouseLocationId
      )
         ? Id
         : null
   }, [selectedItems])

   const selectedCurrentLocationId = useMemo(() => {
      if (selectedItems?.length === 0 || view !== View.Warehouse_Transfer) {
         return null
      }
      const { Warehouse } = selectedItems[0]
      // if all items have the same current location then pass the current location id
      return !!Warehouse &&
         selectedItems.every(
            (item) =>
               item?.Warehouse?.CurrentLocationId ===
               Warehouse?.CurrentLocationId
         )
         ? Warehouse?.CurrentLocationId
         : null
   }, [selectedItems, view])

   const itemsMessage = useMemo(() => {
      const numberOfItems = quantityInCart ?? selectedItems[0]?.quantityForCart
      return numberOfItems === 1 ? 'this item' : `these ${numberOfItems} items`
   }, [selectedItems, quantityInCart])

   const isQuantityOrConsumableItem = useMemo(
      () =>
         !isActionsPage &&
         selectedItems?.some(
            (item) =>
               item.ToolType == ToolTypes['Consumable'] ||
               item.ToolType == ToolTypes['Quantity Item']
         ),
      [selectedItems, isActionsPage]
   )

   const { settings } = useSettings()
   const { list: connections } = useMassTransferConnections(
      connectionRequestType
   )
   const { list: activeLocations } = useActiveLocations(
      view === View.Warehouse_Transfer
         ? { warehousesOnly: true }
         : { jobsOnly: true }
   )
   const {
      list: warehouseOptions,
      isLoading: isWarehousesLoading,
      isSuccess: isWarehousesSuccess,
   } = useWarehouses({
      toolId: selectedItemId,
      currentLocationId: selectedCurrentLocationId,
   })

   const connectionOptions = convertConnectionsToOptions(connections)
   const locationOptions = convertLocationToOptions(activeLocations)

   useEffect(() => {
      if (checkoutAction?.label) {
         setView(checkoutAction?.label)
      }
   }, [checkoutAction])

   useEffect(() => {
      if (handleWorkflowSuccess && !isWorkflowError) {
         handleWorkflowSuccess(workflowResponse)
      }
   }, [isWorkflowPostSuccess, isWorkflowError])

   useEffect(() => {
      switch (view) {
         case View.Loan: {
            const loanMsg = settings.EnableLocationTracking
               ? `You're almost done! Select where to Loan ${itemsMessage}, select a Connection, Location, and a Due Back date if needed.`
               : `You're almost done! Select where to Loan ${itemsMessage}, select a Connection, and a Due Back date if needed.`
            setModalMsg(loanMsg)
            setConfirmButtonMsg('Loan')
            setValidationSchema(loanAndFieldTransferSchema)
            break
         }
         case View.Return:
            setModalMsg('')
            setConfirmButtonMsg('Confirm')
            setValidationSchema(
               settings.EnableLocationTracking
                  ? returnSchema
                  : returnNoWarehouseSchema
            )
            break
         case View.Field_Transfer:
            setModalMsg(
               `You're almost done! To Transfer ${itemsMessage}, select a Due Back date and Location if needed.`
            )
            setConfirmButtonMsg('Transfer')
            setValidationSchema(loanAndFieldTransferSchema)
            break
         case View.Warehouse_Transfer: {
            const message = isQuantityOrConsumableItem ? (
               <>
                  You're almost done! To Transfer {itemsMessage}{' '}
                  {selectedWarehouse ? 'from ' : ''}
                  <strong>{selectedWarehouse?.label}</strong> to{' '}
                  <strong>{selectedTransferToWarehouse?.label}</strong>, please
                  click Confirm.
               </>
            ) : (
               `You're almost done! To Transfer ${itemsMessage}, please select a Location.`
            )
            setModalMsg(message)
            const buttonLabel = isQuantityOrConsumableItem
               ? 'Confirm'
               : 'Transfer'
            setConfirmButtonMsg(buttonLabel)
            const schema = isQuantityOrConsumableItem
               ? warehouseTransferNoLocationSchema
               : warehouseTransferSchema
            setValidationSchema(schema)
            break
         }
         case View.Borrow: {
            const borrowMsg = settings.EnableLocationTracking
               ? `You're almost done! To Borrow ${itemsMessage}, select a new Location and Due Back date if needed.`
               : `You're almost done! To Borrow ${itemsMessage}, select a Due Back date if needed.`
            const valSchema = settings.EnableLocationTracking
               ? borrowSchema
               : borrowNoLocationSchema
            setModalMsg(borrowMsg)
            setConfirmButtonMsg('Borrow')
            setValidationSchema(valSchema)
            break
         }
         case View.MakeUnavailable:
         case View.MakeAvailable: {
            const availableMsg =
               view === View.MakeAvailable ? (
                  `Are you sure you wish to make ${itemsMessage} Available?`
               ) : (
                  <>
                     <p>
                        Are you sure you wish to make {itemsMessage}
                        &nbsp;Unavailable?
                     </p>
                     <p>
                        When unavailable, {itemsMessage} cannot be viewed by
                        employees and cannot be loaned or transferred.
                     </p>
                     {selectedItems[0]?.Quantity > 1 && (
                        <Alert
                           severity={Severity.WARNING}
                           message={`All available quantities of ${itemsMessage} will be made Unavailable from all assigned warehouses.`}
                        />
                     )}
                     {selectedItems[0]?.KitHasItems && (
                        <Alert
                           severity={Severity.ERROR}
                           message="There are items assigned to this kit. Please remove these items from the kit before making it Unavailable."
                        />
                     )}
                  </>
               )
            setModalMsg(availableMsg)
            setConfirmButtonMsg('Confirm')
            setValidationSchema(availableSchema)
            break
         }
         default:
            return
      }
   }, [view, isQuantityOrConsumableItem, settings?.EnableLocationTracking])

   useEffect(() => {
      if (isWarehousesSuccess && warehouseOptions?.length > 0) {
         const preSelectedId =
            warehouseOptions.find((opt) => opt?.Selected === true)?.Value ??
            null

         if (view === View.Return) {
            setCartOptions({
               ...cartOptions,
               returnLocationId: preSelectedId,
            })
         }

         if (view === View.Warehouse_Transfer) {
            setCartOptions({
               ...cartOptions,
               selectedLocationId: preSelectedId,
            })
         }
      }
   }, [warehouseOptions, isWarehousesSuccess, view])

   const selectedReturnWarehouse = useMemo(() => {
      const selectedOption = cartOptions.returnLocationId
         ? warehouseOptions.find(
              (opt) => opt?.Value === cartOptions.returnLocationId
           )
         : null
      return convertWarehouseOptionToOption(selectedOption)
   }, [cartOptions?.returnLocationId, warehouseOptions])

   const selectedTransferToWarehouseOption = useMemo(() => {
      const selectedOption = cartOptions.selectedLocationId
         ? warehouseOptions.find(
              (opt) => opt?.Value === cartOptions.selectedLocationId
           )
         : null
      return convertWarehouseOptionToOption(selectedOption)
   }, [cartOptions?.selectedLocationId, warehouseOptions])

   const prepareDataForAPI = (items: Item[], cartOptions: CartOptions) => {
      switch (view) {
         case View.Loan:
         case View.Borrow:
            return {
               postData: {
                  returnDate: cartOptions?.dueBackDate,
                  toolLoanIdAndQuantity: [
                     ...items.map((item) => ({
                        toolId: item.Id,
                        quantity: item?.quantityForCart ?? 1,
                        toolWarehouseId:
                           selectedWarehouse?.toolWarehouseId ??
                           item?.Warehouse?.ToolWarehouseID,
                        comments: cartOptions?.comment,
                     })),
                  ],
                  borrowingUserId: cartOptions?.connection,
                  location: cartOptions?.location,
                  selectedLocationId: cartOptions?.selectedLocationId,
                  comments: cartOptions?.comment,
               },
               path: PATH.WORKFLOW.LOAN_BORROW,
            }
         case View.Return: {
            return {
               postData: {
                  toolLoanIdAndQuantity: [
                     ...items.map((item) => ({
                        toolId: item.Id,
                        quantity: item?.quantityForCart ?? 1,
                        toolLoanId: item?.ToolLoanId,
                        returnLocationId: cartOptions?.returnLocationId
                           ? Number(cartOptions.returnLocationId)
                           : null,
                        comments: cartOptions?.comment,
                     })),
                  ],
               },
               path: PATH.WORKFLOW.RETURN,
            }
         }
         case View.Warehouse_Transfer: {
            return {
               postData: {
                  location:
                     selectedTransferToWarehouse?.id ?? cartOptions?.location,
                  selectedLocationId:
                     selectedTransferToWarehouse?.id ??
                     cartOptions?.selectedLocationId,
                  toolLoanIdAndQuantity: [
                     ...items.map((item) => ({
                        toolId: item.Id,
                        toolWarehouseId:
                           selectedWarehouse?.toolWarehouseId ??
                           item?.Warehouse?.ToolWarehouseID,
                        Quantity: item?.quantityForCart,
                        ToolLoanId: item?.ToolLoanId,
                        comments: cartOptions?.comment,
                     })),
                  ],
               },
               path: PATH.WORKFLOW.WAREHOUSE_TRANSFER,
            }
         }
         case View.Field_Transfer: {
            return {
               postData: {
                  returnDate: cartOptions?.dueBackDate,
                  toolLoanIdAndQuantity: [
                     ...items.map((item) => ({
                        toolId: item.Id,
                        quantity: item?.quantityForCart ?? 1,
                        toolLoanId: item?.ToolLoanId,
                        toolWarehouseId:
                           item?.Warehouse?.ToolWarehouseID ?? null,
                        comments: cartOptions?.comment,
                     })),
                  ],
                  borrowingUserId: cartOptions?.connection,
                  location: cartOptions?.location,
                  selectedLocationId: cartOptions?.selectedLocationId,
                  comments: cartOptions?.comment,
               },
               path: PATH.WORKFLOW.FIELD_TRANSFER,
            }
         }
         case View.MakeAvailable:
         case View.MakeUnavailable: {
            return {
               postData: {
                  Available: view === View.MakeAvailable,
                  ToolIds: [...items.map((item) => item.Id)],
               },
               path: PATH.ITEMS.SET_AVAILABILITY,
            }
         }
      }
   }

   const { onSubmit, validationErrors } = useForm(
      validationSchema,
      (values) => {
         if (values) {
            const { postData, path } = prepareDataForAPI(
               selectedItems,
               cartOptions
            )
            postWorkflowRequest(path, postData)
         }
      }
   )

   const handleDropdownChange = (value: string, option: Option) => {
      switch (value) {
         case 'Connection':
            setConnectionName(option?.label)
            setCartOptions((prevCartOptions) => ({
               ...prevCartOptions,
               connection: option?.value,
            }))
            break
         case 'Location':
            setCartOptions((prevCartOptions) => ({
               ...prevCartOptions,
               location: option?.value,
               selectedLocationId: option?.value,
            }))
            break
         case 'Warehouse':
            setCartOptions((prevCartOptions) => ({
               ...prevCartOptions,
               returnLocationId: option?.value,
            }))
            break
         default:
            return
      }
   }

   const handleDateChange = (value: string, date: any) => {
      if (value === 'DueBack') {
         const formattedDate = formatDateForDisplay(date)
         setDueBackDateString(formattedDate)
         setCartOptions((prevState) => {
            return {
               ...prevState,
               dueBackDate: date.toISOString(),
            }
         })
      }
   }

   const handleCommentChange = (value: string, comment: SyntheticInputEvent) => {
      const commentValue = comment.target.value
      setCartOptions((prevState) => {
         return {
            ...prevState,
            comment: commentValue || null
         }
      })
   }

   const handleCloseAfterSuccess = () => {
      handlePageCancelAndReset()
      handleClose()
   }

   const handleFormatForExport = () => {
      const isLoanRequest =
         connectionRequestType === ConnectionRequestType.LoanRequest
      const itemsToExport = selectedItems.map((item) => {
         const currentLocation =
            !isLoanRequest && selectedTransferToWarehouse
               ? selectedTransferToWarehouse?.label
               : locationOptions.find(
                    (loc) => loc.value === cartOptions.selectedLocationId
                 )?.label ?? item.CurrentLocation
         // this formatting is required to show correct data in excel
         return {
            ...item,
            Status: isLoanRequest ? ToolStatus.LOANED : item.Status,
            StatusDesc: isLoanRequest ? 'Loaned' : item.StatusDesc,
            StatusQuantity: item.quantityForCart ?? 1,
            AssignedToUser: isLoanRequest
               ? connectionName
               : item.AssignedToUser,
            Quantity: selectedWarehouse?.quantity ?? item.Quantity, // total quantity
            ReorderLevelQuantity:
               selectedWarehouse?.reorderLevel ?? item.ReorderLevelQuantity,
            CurrentLocation: currentLocation,
            DefaultLocation: selectedWarehouse?.label ?? item?.DefaultLocation,
            Comments: item.Comments || cartOptions?.comment,
         }
      })

      return itemsToExport;
   }

   const handleExportToExcel = () => {
      const itemsToExport = handleFormatForExport()

      const excelData = generateExcelData(itemsToExport, settings)
      exportToExcel(excelData)
   }

   const handlePrint = () => {
      reactToPrintFn();
   }

   const isWorkflowComplete = isWorkflowPostSuccess || isWorkflowError

   const renderPrintableDocument = () => {
      // Only render printable document if we have a successful post
      if (isWorkflowPostSuccess && !isWorkflowError) {
         const warehouseTransferData = view === View.Warehouse_Transfer 
         ? {
            comments: cartOptions?.comment,
            transferToLocationId: selectedTransferToWarehouse?.id || cartOptions?.location || selectedTransferToWarehouseOption.value,
            // Note that request type is injeccted in the PrintableShipTicket component
            items: selectedItems.map((item) => ({
               toolId: item.Id,
               toolWarehouseId: selectedWarehouse?.toolWarehouseId ?? item?.Warehouse?.ToolWarehouseID,
               quantity: item?.quantityForCart,
            }))
         }
         : null

         return (
            <PrintableShipTicket 
               warehouseTransferData={warehouseTransferData}
               contentRef={printContentRef}
               settings={settings}
               view={view as View}
               workflowResponse={workflowResponse}
            />
         )
      }

      return null
   }


   const modalTitle = useMemo(() => {
      if (isWorkflowError) {
         return `${view} Failed`
      } else if (isWorkflowPostSuccess) {
         return `${view} Complete`
      } else if (view === View.MakeAvailable || view === View.MakeUnavailable) {
         return view
      } else {
         return viewTitle ? `${view} Options` : 'Cart Options'
      }
   }, [viewTitle, isWorkflowPostSuccess, isWorkflowError])

   const renderBorrowView = () => {
      if (view !== View.Borrow) return null;
      if (isWorkflowPostSuccess || isWorkflowError) return null
      if (!isWorkflowComplete) return (
         <BorrowView
            handleDropdownChange={handleDropdownChange}
            locationOptions={locationOptions}
            handleDateChange={handleDateChange}
            handleCommentChange={handleCommentChange}
            cartOptions={cartOptions}
            enableLocationTracking={settings?.EnableLocationTracking}
         />
      )

      return (
         <CommentOnlyView
            handleCommentChange={handleCommentChange}
         />
      )
   }
   
   const renderReturnView = () => {
      if (view !== View.Return) return null
      if (isWorkflowPostSuccess || isWorkflowError) return null
      if (!isWorkflowComplete) return (
         <ReturnView
            warehouseOptions={warehouseOptions}
            validationErrors={validationErrors}
            handleCommentChange={handleCommentChange}
            handleDropdownChange={handleDropdownChange}
            selectedReturnWarehouse={selectedReturnWarehouse}
            enableLocationTracking={settings?.EnableLocationTracking}
         />
      )

      return (
         <CommentOnlyView
            handleCommentChange={handleCommentChange}
         />
      )
   }

   const renderWarehouseTransferView = () => {
      if (view !== View.Warehouse_Transfer) return null
      if (isWorkflowPostSuccess || isWorkflowError) return null
      if (view === View.Warehouse_Transfer &&
            !isWorkflowComplete &&
            !isQuantityOrConsumableItem ) return (
               <WarehouseTransferView
                  validationErrors={validationErrors}
                  handleDropdownChange={handleDropdownChange}
                  handleCommentChange={handleCommentChange}
                  warehouseOptions={warehouseOptions}
                  selectedTransferToWarehouseOption={
                     selectedTransferToWarehouseOption
                  }
               />
            )
      
      return (
         <CommentOnlyView
            handleCommentChange={handleCommentChange}
         />
      )
   }

   const renderLoanAndFieldTransferView = () => {
      if (view !== View.Loan && view !== View.Field_Transfer) return null
      if (isWorkflowPostSuccess || isWorkflowError) return null
      if (!isWorkflowComplete) return (
         <LoanAndFieldTransferView
            validationErrors={validationErrors}
            handleDropdownChange={handleDropdownChange}
            connectionOptions={connectionOptions}
            locationOptions={locationOptions}
            handleDateChange={handleDateChange}
            handleCommentChange={handleCommentChange}
            cartOptions={cartOptions}
            enableLocationTracking={
               settings?.EnableLocationTracking
            }
         />
      )

      return (
         <CommentOnlyView
            handleCommentChange={handleCommentChange}
         />
      )
   }

   return (
      <>
         <Modal
            isModalVisible={isModalOpen}
            closeModal={
               // close action should be different if we have completed the action
               (isWorkflowPostSuccess && !isWorkflowError)
                  ? handleCloseAfterSuccess
                  : handleClose 
            }
            title={modalTitle}
            zIndex="999"
            stickyHeader
         >
            {isWarehousesLoading && <LoadingOverlay />}
            <form id="checkout-modal" noValidate onSubmit={onSubmit}>
               <div className={styles.modalMessageWrapper}>
                  {!isWorkflowComplete && <>{modalMsg}</>}
               </div>

               <section
                  className={`${styles.modalBodyWrapper} ${
                     view === View.MakeAvailable ||
                     view === View.MakeUnavailable ||
                     (view === View.Warehouse_Transfer &&
                        isQuantityOrConsumableItem)
                        ? styles.smallBodyWrapper
                        : ''
                  }`}
               >
                  {isPostingRequest && <LoadingOverlay />}

                  {renderLoanAndFieldTransferView()}
                  

                  {renderReturnView()}
                  

                  {renderWarehouseTransferView()}
                  

                  {renderBorrowView()}

                  {isWorkflowPostSuccess && !isWorkflowError && (
                     <SuccessView
                        itemCount={quantityInCart ?? selectedItems.length}
                        view={view}
                        connectionName={connectionName}
                        dueBackDate={dueBackDateString}
                        isAdminCompanyUser={isAdminCompanyUser}
                     />
                  )}

                  {isWorkflowError && <ErrorView view={view} />}
               </section>
               <div className={styles.buttonRow}>
                  {!isWorkflowComplete && (
                     <Row>
                        <Col xs={12} sm={6}>
                           <div className={styles.buttonWrapper}>
                              <Button
                                 variant="primary"
                                 type="submit"
                                 form="checkout-modal"
                                 disabled={
                                    view === View.MakeUnavailable &&
                                    selectedItems[0]?.KitHasItems
                                 }
                              >
                                 {confirmButtonMsg}
                              </Button>
                           </div>
                        </Col>
                        <Col xs={12} sm={6}>
                           <div className={styles.buttonWrapper}>
                              <Button
                                 variant={'tertiary'}
                                 onClick={handleClose}
                                 type="button"
                                 isReset
                              >
                                 Go Back
                              </Button>
                           </div>
                        </Col>
                     </Row>
                  )}
                  {isWorkflowPostSuccess && !isWorkflowError && (
                     <Row>
                        <Col xs={12} sm={4}>
                           <div className={styles.buttonWrapper}>
                              <Button
                                 variant={'primary'}
                                 type="button"
                                 onClick={handleCloseAfterSuccess}
                                 isReset
                              >
                                 Close
                              </Button>
                           </div>
                        </Col>
                        <Col xs={12} sm={4}>
                           <div className={styles.buttonWrapper}>
                              <Button
                                 variant="success"
                                 onClick={handleExportToExcel}
                                 type="button"
                                 isReset
                                 icon={
                                    <Svg
                                       id="export-row"
                                       className={styles.excelIcon}
                                    />
                                 }
                              >
                                 Export
                              </Button>
                           </div>
                        </Col>
                        <Col xs={12} sm={4}>
                           <div className={styles.buttonWrapper}>
                              <Button
                                 variant="success"
                                 onClick={handlePrint}
                                 type="button"
                                 isReset
                              >
                                 Print 
                              </Button>
                           </div>
                        </Col>
                     </Row>
                  )}
                  {isWorkflowError && (
                     <Row>
                        <Col xs={12} sm={6}>
                           <div className={styles.buttonWrapper}>
                              <Button
                                 variant={'tertiary'}
                                 type="button"
                                 onClick={handleClose}
                                 isReset
                              >
                                 Close
                              </Button>
                           </div>
                        </Col>
                     </Row>
                  )}
               </div>
            </form>
         </Modal>
         {renderPrintableDocument()}
      </>
   )
}

export default CheckoutModal
